import { Injectable, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subscription, interval, of } from 'rxjs';
import { switchMap, catchError, tap, startWith } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

type OriginStatusResponse = {
  enabled: boolean;
  [key: string]: unknown;
};

/**
 * Service that polls the origin status endpoint to check if the chat is enabled.
 * Polls every 30 seconds and exposes the enabled state as an observable.
 */
@Injectable({
  providedIn: 'root',
})
export class OriginStatusService implements OnDestroy {
  private readonly apiBaseUrl = environment.apiUrl;
  private readonly pollIntervalMs = 30000; // 30 seconds

  private readonly _enabled = new BehaviorSubject<boolean | null>(null);
  private pollingSubscription: Subscription | null = null;

  constructor(private http: HttpClient) {}

  /**
   * Observable stream of the enabled status.
   * Emits null initially (unknown state), then true/false based on API response.
   */
  get enabled$(): Observable<boolean | null> {
    return this._enabled.asObservable();
  }

  /**
   * Get the current enabled state synchronously.
   */
  get isEnabled(): boolean | null {
    return this._enabled.getValue();
  }

  /**
   * Start polling the origin status endpoint.
   * Makes an immediate request, then polls every 30 seconds.
   */
  startPolling(origin: string): void {
    if (this.pollingSubscription) {
      return; // Already polling
    }

    const endpoint = `${this.apiBaseUrl}/api/v1/origin/${origin}`;

    this.pollingSubscription = interval(this.pollIntervalMs)
      .pipe(
        startWith(0), // Make an immediate request
        switchMap(() =>
          this.http.get<OriginStatusResponse>(endpoint).pipe(
            catchError((error) => {
              console.error('Origin status polling error:', error);
              return of(null);
            })
          )
        ),
        tap((response) => {
          if (response !== null) {
            this._enabled.next(response.enabled);
          }
        })
      )
      .subscribe();
  }

  /**
   * Stop polling the origin status endpoint.
   */
  stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
      this.pollingSubscription = null;
    }
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }
}